/**
 * Utilitários para organização de parcelamentos
 */

import { checkOverdueStatus } from './dateUtils';

interface FinancialEntry {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category: string;
  payment_date?: string;
  status: string;
  created_at: string;
  client_id?: string;
  case_id?: string;
  is_installment?: boolean;
  installments_count?: number;
  installment_frequency?: string;
  down_payment?: number;
  is_conditional_payment?: boolean;
  conditional_type?: string;
  actual_payment_date?: string;
  is_overdue?: boolean;
  overdue_days?: number;
  rescheduled_count?: number;
  last_reschedule_date?: string;
  payment_notes?: string;
  clients?: {
    name: string;
    email: string;
  };
  cases?: {
    title: string;
    case_number?: string;
  };
}

interface InstallmentGroup {
  baseDescription: string;
  totalAmount: number;
  totalInstallments: number;
  nextPayment: FinancialEntry | null;
  overduePayment: FinancialEntry | null;
  allInstallments: FinancialEntry[];
  isExpanded: boolean;
}

/**
 * Extrai a descrição base de um parcelamento (remove a parte X/Y)
 */
export const extractBaseDescription = (description: string): string => {
  const match = description.match(/^(.*?)\s*\(\d+\/\d+\)$/);
  return match ? match[1].trim() : description;
};

/**
 * Verifica se uma entrada é parte de um parcelamento
 */
export const isInstallmentEntry = (entry: FinancialEntry): boolean => {
  return entry.is_installment === true && 
         entry.installments_count && 
         entry.installments_count > 1;
};

/**
 * Agrupa entradas em parcelamentos
 */
export const groupInstallments = (entries: FinancialEntry[]): {
  installmentGroups: InstallmentGroup[];
  regularEntries: FinancialEntry[];
} => {
  const installmentGroups = new Map<string, InstallmentGroup>();
  const regularEntries: FinancialEntry[] = [];

  entries.forEach(entry => {
    if (isInstallmentEntry(entry)) {
      const baseDesc = extractBaseDescription(entry.description);
      const key = `${baseDesc}_${entry.installments_count}_${entry.installment_frequency}`;

      if (!installmentGroups.has(key)) {
        installmentGroups.set(key, {
          baseDescription: baseDesc,
          totalAmount: 0,
          totalInstallments: entry.installments_count || 1,
          nextPayment: null,
          overduePayment: null,
          allInstallments: [],
          isExpanded: false
        });
      }

      const group = installmentGroups.get(key)!;
      group.allInstallments.push(entry);
      group.totalAmount += entry.amount;

      // Verificar se é vencida ou próxima
      const { isOverdue } = checkOverdueStatus(entry.payment_date || '');
      
      if (isOverdue && entry.status === 'Pendente') {
        if (!group.overduePayment || 
            new Date(entry.payment_date || '') < new Date(group.overduePayment.payment_date || '')) {
          group.overduePayment = entry;
        }
      } else if (!isOverdue && entry.status === 'Pendente') {
        if (!group.nextPayment || 
            new Date(entry.payment_date || '') < new Date(group.nextPayment.payment_date || '')) {
          group.nextPayment = entry;
        }
      }
    } else {
      regularEntries.push(entry);
    }
  });

  return {
    installmentGroups: Array.from(installmentGroups.values()),
    regularEntries
  };
};

/**
 * Obtém a entrada principal para exibição (próxima ou vencida)
 */
export const getMainInstallmentEntry = (group: InstallmentGroup): FinancialEntry => {
  // Priorizar parcela vencida
  if (group.overduePayment) {
    return group.overduePayment;
  }
  
  // Se não há vencida, usar próxima
  if (group.nextPayment) {
    return group.nextPayment;
  }
  
  // Fallback: primeira parcela pendente
  const pendingInstallment = group.allInstallments.find(entry => entry.status === 'Pendente');
  if (pendingInstallment) {
    return pendingInstallment;
  }
  
  // Fallback: primeira parcela
  return group.allInstallments[0];
};

/**
 * Cria uma entrada consolidada para exibição
 */
export const createConsolidatedEntry = (group: InstallmentGroup): FinancialEntry => {
  const mainEntry = getMainInstallmentEntry(group);
  const pendingCount = group.allInstallments.filter(e => e.status === 'Pendente').length;
  
  // Encontrar a posição da parcela atual (baseada na data de pagamento)
  const sortedInstallments = group.allInstallments.sort((a, b) => 
    new Date(a.payment_date || '').getTime() - new Date(b.payment_date || '').getTime()
  );
  
  const currentInstallmentIndex = sortedInstallments.findIndex(entry => entry.id === mainEntry.id);
  const currentInstallmentNumber = currentInstallmentIndex + 1;
  
  return {
    ...mainEntry,
    description: `${group.baseDescription} (${currentInstallmentNumber}/${group.totalInstallments} pendentes)`,
    amount: group.totalAmount,
    // Manter dados da parcela principal para exibição
    payment_date: mainEntry.payment_date,
    status: mainEntry.status,
    // Adicionar metadados do grupo
    payment_notes: `Parcelamento: ${group.totalInstallments} parcelas de R$ ${(group.totalAmount / group.totalInstallments).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
  };
}; 